import type { SupabaseClient } from '@supabase/supabase-js';
import { z } from 'zod';

import type { Database } from '@/integrations/supabase/types';

export const ParsedFileResultSchema = z.object({
  filename: z.string(),
  content: z.string(),
  sizeBytes: z.number(),
});

export const FileContextSchema = z.object({
  id: z.string(),
  filename: z.string(),
  storagePath: z.string().optional(),
  storageBucket: z.string().optional(),
  fileSize: z.number().optional(),
  fileType: z.string().optional(),
  documentId: z.string().uuid().optional(),
});

export type ParsedFileResult = z.infer<typeof ParsedFileResultSchema>;
export type FileContext = z.infer<typeof FileContextSchema>;

export const parseSingleFile = async (
  file: File,
  supabase: SupabaseClient<Database>
): Promise<ParsedFileResult> => {
  const formData = new FormData();
  formData.append('file', file);

  const { data, error } = await supabase.functions.invoke(
    'stella-api/files/parse',
    { body: formData }
  );

  if (error) {
    const serverError = (error as { context?: { error?: string } }).context
      ?.error;
    throw new Error(serverError || `Failed to parse "${file.name}"`);
  }

  if (!data?.content) {
    throw new Error(`No content returned for "${file.name}"`);
  }

  return {
    filename: file.name,
    content: data.content,
    sizeBytes: file.size,
  };
};

export const parseAttachedFiles = async (
  files: File[],
  supabase: SupabaseClient<Database>
): Promise<ParsedFileResult[]> => {
  return Promise.all(files.map((file) => parseSingleFile(file, supabase)));
};
